const services = [
  {
    id: "design",
    icon: "ri-layout-masonry-line",
    title: "Website Design",
    tagline: "First impressions that convert",
    description: "Your website is your most powerful sales tool. We design custom, brand-aligned websites that don't just look stunning — they're strategically crafted to guide visitors toward taking action.",
    visualIcon: "ri-layout-masonry-line",
    visualColor: "#d4af37",
    features: [
      { icon: "ri-palette-line", title: "Custom UI/UX Design", desc: "Every element designed from scratch to match your brand and business goals." },
      { icon: "ri-smartphone-line", title: "Mobile-First Approach", desc: "Flawless experience across all devices — desktop, tablet, and mobile." },
      { icon: "ri-eye-line", title: "Conversion-Focused Layouts", desc: "Strategic placement of CTAs, trust signals, and content hierarchy." },
      { icon: "ri-draft-line", title: "Wireframing & Prototyping", desc: "See your website before it's built with interactive prototypes." },
    ],
  },
  {
    id: "development",
    icon: "ri-code-s-slash-line",
    title: "Website Development",
    tagline: "Fast, clean, and built to last",
    description: "Beautiful design means nothing if your site is slow or breaks. We build with modern frameworks that deliver blazing-fast performance, rock-solid reliability, and clean code that scales.",
    visualIcon: "ri-code-s-slash-line",
    visualColor: "#6a9fd8",
    features: [
      { icon: "ri-reactjs-line", title: "Modern Frameworks", desc: "Built with React, Next.js, and other cutting-edge technologies." },
      { icon: "ri-speed-line", title: "Performance Optimized", desc: "Sub-second load times that keep visitors engaged and boost SEO." },
      { icon: "ri-database-2-line", title: "CMS Integration", desc: "Easy-to-manage content systems so you can update your site yourself." },
      { icon: "ri-shield-check-line", title: "Secure & Reliable", desc: "SSL, security best practices, and reliable hosting setup included." },
    ],
  },
  {
    id: "seo",
    icon: "ri-search-eye-line",
    title: "SEO Optimization",
    tagline: "Get found by the right people",
    description: "Ranking on Google isn't luck — it's strategy. We optimize every aspect of your website to attract organic traffic from people actively searching for what you offer.",
    visualIcon: "ri-search-eye-line",
    visualColor: "#6abf6a",
    features: [
      { icon: "ri-file-search-line", title: "Technical SEO Audit", desc: "Deep analysis of your site's technical health and ranking factors." },
      { icon: "ri-article-line", title: "On-Page Optimization", desc: "Keyword strategy, meta tags, content structure, and internal linking." },
      { icon: "ri-speed-mini-line", title: "Core Web Vitals", desc: "Optimize for Google's performance metrics that directly impact rankings." },
      { icon: "ri-map-pin-line", title: "Local SEO Strategy", desc: "Dominate local search results and attract nearby customers." },
    ],
  },
  {
    id: "cro",
    icon: "ri-line-chart-line",
    title: "Conversion Optimization",
    tagline: "Turn more visitors into clients",
    description: "Getting traffic is only half the battle. We analyze how visitors behave on your site and make data-driven improvements that turn more of them into paying customers.",
    visualIcon: "ri-line-chart-line",
    visualColor: "#c47fd4",
    features: [
      { icon: "ri-test-tube-line", title: "A/B Testing", desc: "Test different versions of pages to find what converts best." },
      { icon: "ri-user-search-line", title: "User Behavior Analysis", desc: "Heatmaps, session recordings, and funnel analysis to understand visitors." },
      { icon: "ri-cursor-line", title: "CTA Strategy", desc: "Optimize button placement, copy, and design for maximum clicks." },
      { icon: "ri-bar-chart-2-line", title: "Ongoing Reporting", desc: "Monthly reports showing exactly how your conversions are improving." },
    ],
  },
];

export default function ServiceDetail() {
  return (
    <section className="py-20 bg-[#080b18]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 space-y-24">
        {services.map((service, index) => (
          <div
            key={service.id}
            className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center"
          >
            {/* Visual Panel */}
            <div className={`relative ${index % 2 === 1 ? "lg:order-2" : ""}`}>
              <div className="rounded-2xl overflow-hidden border border-white/5 bg-gradient-to-br from-[#0c0f1c] to-[#080b18] flex items-center justify-center min-h-[320px] p-10">
                <div className="text-center">
                  <div
                    className="w-24 h-24 flex items-center justify-center rounded-2xl mx-auto mb-6"
                    style={{ backgroundColor: `${service.visualColor}15`, border: `1px solid ${service.visualColor}30` }}
                  >
                    <i className={`${service.visualIcon} text-5xl`} style={{ color: service.visualColor }}></i>
                  </div>
                  <div className="text-white font-black text-2xl mb-2">{service.title}</div>
                  <div className="text-white/40 text-sm">{service.tagline}</div>
                  <div className="flex items-center justify-center gap-3 mt-6">
                    {service.features.map((f) => (
                      <div key={f.title} className="w-10 h-10 flex items-center justify-center rounded-xl bg-white/5 border border-white/8">
                        <i className={`${f.icon} text-white/40 text-base`}></i>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className={index % 2 === 1 ? "lg:order-1" : ""}>
              <div className="flex items-center gap-3 mb-5">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#d4af37]/10 border border-[#d4af37]/20">
                  <i className={`${service.icon} text-[#d4af37] text-xl`}></i>
                </div>
                <span className="text-[#d4af37] text-xs font-semibold tracking-widest uppercase">{service.tagline}</span>
              </div>

              <h2 className="text-3xl md:text-4xl font-black text-white mb-4">{service.title}</h2>
              <p className="text-white/50 text-base leading-relaxed mb-8">{service.description}</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {service.features.map((feature) => (
                  <div key={feature.title} className="p-4 rounded-xl border border-white/5 bg-[#0c0f1c] hover:border-[#d4af37]/20 transition-colors duration-200">
                    <div className="flex items-center gap-2 mb-2">
                      <i className={`${feature.icon} text-[#d4af37] text-base`}></i>
                      <span className="text-white text-sm font-semibold">{feature.title}</span>
                    </div>
                    <p className="text-white/40 text-xs leading-relaxed">{feature.desc}</p>
                  </div>
                ))}
              </div>

              <a
                href="/#contact"
                className="inline-flex items-center gap-2 mt-8 px-6 py-3 rounded-full bg-[#d4af37] text-[#050810] font-bold text-sm hover:bg-[#e8c84a] transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                Get Started <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
